import React, { useState, useEffect, useRef, useContext } from 'react';
import {
  Container,
  ChatBox,
  Header,
  InputTextarea,
  Message,
  MessageBubble,
  CloseButton,
  CollapsedChatIcon,
  NotificationBadge,
  TypingIndicator,
  TypingDots,
  FloatingChatText,
  HoverDiv,
  SolisAvatar,
  UserAvatar,
} from './styles';
import { api } from '../../providers/apiClient';
import { ContextUser } from '../../providers/ContextUser';
import { playNotificationSound, requestAudioPermission, testNotificationSound } from '../../utils';
import avatarChat from '../../assets/imgs/avatar-chat.png';

const Chat = () => {
  const { currentUser } = useContext(ContextUser);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [showHoverDiv, setShowHoverDiv] = useState(false);
  const chatBoxRef = useRef(null);
  const [audioPermissionGranted, setAudioPermissionGranted] = useState(false);

  const sendMessage = async () => {
    if (input.trim() === '') return;

    const newMessage = { role: 'user', content: input };
    const currentInput = input;
    setInput('');
    setIsTyping(true); // Inicia o indicador de digitando

    // Adiciona apenas a mensagem do usuário primeiro
    setMessages(prevMessages => [...prevMessages, newMessage]);

    // Extrai o primeiro nome do usuário
    const firstName = currentUser?.name?.split(' ')[0] || 'Usuário';
    
    const payload = { 
      message: currentInput,
      userName: firstName 
    };

    try {
      const response = await api.post('/chatbot', payload);
      const botMessage = { role: 'assistant', content: response.data.reply };
      
      // Adiciona um delay mínimo de 2 segundos para o indicador de digitando
      setTimeout(() => {
        // Adiciona apenas a resposta da IA
        setMessages(prevMessages => [...prevMessages, botMessage]);
        setIsTyping(false); // Para o indicador de digitando
        
        // Toca o som de notificação quando a SolisAI responde
        playNotificationSound();
      }, 1500); // 2 segundos de delay mínimo
      
    } catch (error) {
      console.error('Erro na API', error.response.data.error);
      
      // Adiciona um delay mínimo mesmo em caso de erro
      setTimeout(() => {
        // Adiciona mensagem de erro
        const errorMessage = { role: 'assistant', content: 'Desculpe, ocorreu um erro. Tente novamente.' };
        setMessages(prevMessages => [...prevMessages, errorMessage]);
        setIsTyping(false); // Para o indicador de digitando
        
        // Toca o som de notificação mesmo em caso de erro
        playNotificationSound();
      }, 1500); // 2 segundos de delay mínimo
    }
  };

  useEffect(() => {
    if (chatBoxRef.current) {
      chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
    }
  }, [messages, isTyping]);

  // Função para solicitar permissão de áudio quando o usuário interagir
  const handleAudioPermissionRequest = async () => {
    if (!audioPermissionGranted) {
      const granted = await requestAudioPermission();
      setAudioPermissionGranted(granted);
      if (granted) {
        console.log('Permissão de áudio concedida');
      }
    }
  };

  // Solicita permissão quando o usuário abrir o chat
  const handleToggleChat = () => {
    if (!isOpen) {
      handleAudioPermissionRequest();
    }
    setIsOpen(!isOpen);
  };

  // Adiciona mensagem de boas-vindas personalizada quando o chat é aberto
  useEffect(() => {
    if (isOpen && messages.length === 0 && currentUser?.name) {
      const firstName = currentUser.name.split(' ')[0];
      const welcomeMessage = {
        role: 'assistant',
        content: `👋 Olá ${firstName}! Sou a SolisAI, sua assistente especializada em LGPD para entidades públicas brasileiras.\n\n🔒 Como posso ajudá-lo hoje com questões de proteção de dados e compliance?`
      };
      setMessages([welcomeMessage]);
    }
  }, [isOpen, messages.length, currentUser]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Ícone colapsado - visível quando chat fechado */}
      {!isOpen && (
        <>
          <CollapsedChatIcon
            onClick={handleToggleChat}
            isOpen={isOpen}
            onMouseEnter={() => setShowHoverDiv(true)}
            onMouseLeave={() => setShowHoverDiv(false)}
          >
            <div className="chat-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20 2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h4l4 4 4-4h4c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z"/>
              </svg>
            </div>
            {messages.length > 0 && (
              <NotificationBadge>
                {messages.length > 99 ? '99+' : messages.length}
              </NotificationBadge>
            )}
          </CollapsedChatIcon>
          
          {/* Div que aparece ao passar o mouse */}
          {showHoverDiv && (
            <HoverDiv
              onMouseEnter={() => setShowHoverDiv(true)}
              onMouseLeave={() => setShowHoverDiv(false)}
            >
              <span>Fale com a SolisAI...</span>
            </HoverDiv>
          )}
        </>
      )}

             {/* Chat expandido - visível apenas quando aberto */}
       {isOpen && (
        <Container id='chat-container' isOpen={isOpen}>
          <Header
            id='chat-header'
          >
            <div className="header-content">
              <div className="solis-avatar">
                <img src={avatarChat} alt="SolisAI" />
              </div>
              <div className="header-text">
                <span className="title">SolisAI</span>
                <span className="subtitle">Sua assistente para compliance e adequação LGPD</span>
              </div>
            </div>
            <CloseButton onClick={() => setIsOpen(false)}>X</CloseButton>
          </Header>
          <>
            <ChatBox id='chat-box' ref={chatBoxRef} isOpen={isOpen}>
              {messages.map((msg, index) => (
                <Message key={index} isUser={msg.role === 'user'}>
                  {msg.role === 'user' ? (
                    <div id='user-message'>
                      <MessageBubble isUser={msg.role === 'user'}>
                        {msg.content}
                      </MessageBubble>
                      <UserAvatar>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                        </svg>
                      </UserAvatar>
                    </div>
                  ) : (
                    <div id='solis-message'>
                      <SolisAvatar>
                        <img src={avatarChat} alt="SolisAI" />
                      </SolisAvatar>
                      <MessageBubble isUser={msg.role === 'user'}>
                        {msg.content}
                      </MessageBubble>
                    </div>
                  )}
                </Message>
              ))}
              
              {/* Indicador de digitando */}
              {isTyping && (
                <Message isUser={false}>
                  <div id='solis-message'>
                    <SolisAvatar>
                      <img src={avatarChat} alt="SolisAI" />
                    </SolisAvatar>
                    <MessageBubble isUser={false}>
                      <TypingIndicator>
                        <span>SolisAI está digitando</span>
                        <TypingDots>
                          <span></span>
                          <span></span>
                          <span></span>
                        </TypingDots>
                      </TypingIndicator>
                    </MessageBubble>
                  </div>
                </Message>
              )}
            </ChatBox>
            <InputTextarea
              id='chat-input'
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder='Digite aqui...'
              rows='2'
              disabled={isTyping}
            />
          </>
        </Container>
      )}
    </>
  );
};

export default Chat;
